import {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  FirestoreError,
  Query,
  QuerySnapshot,
  Transaction,
  Unsubscribe,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  runTransaction as fbRunTransaction,
  setDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { isTerminalFirestoreError, wrapSubscriptionError } from './subscriptionError';
import { logStreamError } from '../utils/globalErrorLogger';

/**
 * Generic Firestore access used by all feature data services. Each maps raw
 * documents through a `(id, data) => T` mapper so callers work with typed domain
 * objects, never `DocumentData`. Subscriptions route errors through
 * `wrapSubscriptionError` so a dropped listener is logged, never silent.
 */
export type DocMapper<T> = (id: string, data: DocumentData) => T;

const SUBSCRIPTION_RETRY_BASE_MS = 1000;
const SUBSCRIPTION_RETRY_MAX_MS = 10000;

/**
 * Firestore never reconnects a listener on its own — once `onSnapshot`'s
 * error callback fires, that listener is dead for good. Left alone, callers
 * of `subscribeToDoc`/`subscribeToQuery` go stale for the rest of the
 * session after a single transient stream drop (network blip, token refresh
 * race). Re-attach with bounded exponential backoff instead, matching the
 * pattern already proven in `subscribeToUserAccountByFirebaseId`.
 */
function subscribeWithRetry<TSnapshot>(
  attach: (onNext: (snap: TSnapshot) => void, onError: (err: FirestoreError) => void) => Unsubscribe,
  onNext: (snap: TSnapshot) => void,
  context: string,
  onError?: (err: Error) => void,
): Unsubscribe {
  let stopped = false;
  let unsubscribe: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;

  function scheduleRetry() {
    if (stopped || retryTimer !== null) return;
    const delay = Math.min(SUBSCRIPTION_RETRY_BASE_MS * 2 ** retryAttempt, SUBSCRIPTION_RETRY_MAX_MS);
    retryAttempt += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      subscribe();
    }, delay);
  }

  function subscribe() {
    if (stopped) return;
    try {
      unsubscribe = attach(
        (snap) => {
          if (stopped) return;
          retryAttempt = 0;
          onNext(snap);
        },
        wrapSubscriptionError(context, (err) => {
          unsubscribe = null;
          onError?.(err);
          // A permission/query rejection won't change on retry — stop instead
          // of backing off forever against a defect that will never clear.
          if (!isTerminalFirestoreError(err)) scheduleRetry();
        }),
      );
    } catch (err) {
      // `onSnapshot` can also throw *synchronously* — a Firestore instance whose
      // async queue died (INTERNAL ASSERTION FAILED after a failed token
      // refresh), a terminated instance, or a malformed reference. That throw
      // never reaches `wrapSubscriptionError`, so left alone it escapes the
      // caller's effect and blanks the page at the root error boundary. None of
      // those causes clears on a re-attach, so report once and stay stopped.
      unsubscribe = null;
      const error = err instanceof Error ? err : new Error(String(err));
      logStreamError(error, { context });
      onError?.(error);
    }
  }

  subscribe();
  return () => {
    stopped = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
    unsubscribe?.();
    unsubscribe = null;
  };
}

export function subscribeToDoc<T>(
  ref: DocumentReference,
  mapper: DocMapper<T>,
  onNext: (value: T | null) => void,
  options: { context?: string; onError?: (err: Error) => void } = {},
): Unsubscribe {
  const context = options.context ?? `subscribeToDoc:${ref.path}`;
  return subscribeWithRetry<DocumentSnapshot>(
    (handleNext, handleError) => onSnapshot(ref, handleNext, handleError),
    (snap) => onNext(snap.exists() ? mapper(snap.id, snap.data()) : null),
    context,
    options.onError,
  );
}

export function subscribeToQuery<T>(
  q: Query,
  mapper: DocMapper<T>,
  onNext: (values: T[]) => void,
  options: { context?: string; onError?: (err: Error) => void } = {},
): Unsubscribe {
  const context = options.context ?? 'subscribeToQuery';
  return subscribeWithRetry<QuerySnapshot>(
    (handleNext, handleError) => onSnapshot(q, handleNext, handleError),
    (snap) => onNext(snap.docs.map((d) => mapper(d.id, d.data()))),
    context,
    options.onError,
  );
}

export async function getDocOnce<T>(ref: DocumentReference, mapper: DocMapper<T>): Promise<T | null> {
  const snap = await getDoc(ref);
  return snap.exists() ? mapper(snap.id, snap.data()) : null;
}

export async function getQueryOnce<T>(q: Query, mapper: DocMapper<T>): Promise<T[]> {
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapper(d.id, d.data()));
}

/**
 * Create a document whose `id` field holds its own Firestore document id.
 *
 * Collections in this backend retain a denormalized `id` field for schema
 * compatibility, so new writes keep it consistent with the document path. The
 * id is therefore minted client-side and the document is written **once**,
 * already carrying it.
 *
 * The alternative — `addDoc` and then a second write to stamp the id — is what
 * the source app does, and it is why production holds task documents with a
 * permanently blank `id`: nothing repairs the document when that second write
 * doesn't land. It also leaves a window in which readers see a blank id.
 *
 * Prefer this over `addDoc` for any collection carrying an `id` field.
 */
export async function createDoc<T extends Record<string, unknown>>(
  collectionPath: string,
  data: T,
): Promise<{ id: string; data: T & { id: string } }> {
  const ref = doc(collection(db, collectionPath));
  const persisted = { ...data, id: ref.id };
  await setDoc(ref, persisted);
  return { id: ref.id, data: persisted };
}

/** Thin wrapper over Firestore transactions, kept here so callers import one
 *  data module rather than the SDK directly. */
export function runTransaction<T>(updater: (tx: Transaction) => Promise<T>): Promise<T> {
  return fbRunTransaction(db, updater);
}

// NB: dates are stored as ISO-8601 strings (`new Date().toISOString()`), NOT
// Firestore Timestamps — so `serverTimestamp` is intentionally not re-exported.
